import FetchClient, { type ApiResponse } from "./FetchClient";
import { resetLogfrog } from "../utils/logfrog";
import { resetLifecycleReporting } from "./LifecycleService";
import {
  clearAuthTokens,
  getTokensFromStorage,
  refreshAccessToken,
  saveCredsInLocalStorage,
} from "./TokenService";
export { getTokensFromStorage, saveCredsInLocalStorage } from "./TokenService";
import { requestAnonymousChallenge } from "./AnonymousChallengeService";
import {
  clearReferralAfterClaim,
  clearAcquisitionDealAfterSignup,
  clearSignupReferrer,
  getAcquisitionDealForSignup,
  getReferralForSignup,
  getSignupReferrerDomain,
  waitForReferral,
} from "./AttributionService";
// import { User } from "../context/UserContext";

interface SignUpResponse {
  access_token: string;
  refresh_token: string;
}

// export async function refreshToken(refreshToken: string) {
//   const response = await axios.post(
//     "https://uh7n55gea1.execute-api.us-east-1.amazonaws.com/prd/auth/refresh",
//     { refreshToken },
//   );
//   return response.data;
// }

export function logOut() {
  clearAuthTokens();

  resetLogfrog({ rotateVisitor: true });
  resetLifecycleReporting();

  // Redirect to home page
  window.location.href = "/";
}

export async function refresh(refreshToken: string) {
  return refreshAccessToken(refreshToken);
}

export async function lookupEmail(email: string): Promise<{ exists: boolean }> {
  const resp = await fetch(import.meta.env.VITE_API_URL + "/auth/email-lookup", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || "Unable to verify email");
  }
  const json = (await resp.json()) as ApiResponse<{ exists: boolean }>;
  return { exists: !!json.data?.exists };
}

export async function signIn(email: string, password: string) {
  const resp = await fetch(import.meta.env.VITE_API_URL + "/auth/sign-in", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  if (resp.ok) {
    const json = (await resp.json()) as ApiResponse<SignUpResponse>;
    saveCredsInLocalStorage(json.data.access_token, json.data.refresh_token);
    await claimPendingAttribution();
    clearSignupReferrer();
    return true;
  }

  const text = await resp.text();
  throw new Error(text);
}

export async function loginWithGoogle(accessToken: string) {
  const resp = await fetch(import.meta.env.VITE_API_URL + "/auth/google", {
    method: "POST",
    body: JSON.stringify({ access_token: accessToken }),
  });
  if (resp.ok) {
    const json = (await resp.json()) as SignUpResponse;
    saveCredsInLocalStorage(json.access_token, json.refresh_token);
    return true;
  }
  const text = await resp.text();
  throw new Error(text);
}

export async function signUp(email: string, password: string, marketingEmailsConsent?: boolean) {
  const referralId = getReferralForSignup();
  const acquisitionDealId = getAcquisitionDealForSignup();
  const signupReferrerDomain = getSignupReferrerDomain();

  const body: {
    email: string;
    password: string;
    name: string;
    referralId?: string;
    acquisitionDealId?: string;
    signupReferrerDomain?: string;
    marketingEmailsConsent?: boolean;
  } = {
    email,
    password,
    name: "",
  };
  if (marketingEmailsConsent) body.marketingEmailsConsent = true;

  if (referralId) {
    body.referralId = referralId;
  }
  if (acquisitionDealId) body.acquisitionDealId = acquisitionDealId;
  if (signupReferrerDomain) body.signupReferrerDomain = signupReferrerDomain;
  const postSignUp = (captchaToken?: string) =>
    fetch(import.meta.env.VITE_API_URL + "/auth/sign-up?source=Web", {
      method: "POST",
      body: JSON.stringify(body),
      headers: captchaToken ? { "X-DealHawk-Captcha-Token": captchaToken } : undefined,
    });
  let resp = await postSignUp();
  // Sign-ups are capped per IP; past the cap the API asks for Turnstile.
  if (resp.status === 429) {
    const challenge = await resp.clone().json().catch(() => null);
    if (challenge?.code === "captcha_required" && challenge.captcha?.siteKey) {
      const token = await requestAnonymousChallenge({
        provider: "turnstile",
        siteKey: challenge.captcha.siteKey,
        action: challenge.captcha.action || "public_catalog",
      });
      resp = await postSignUp(token);
    }
  }
  if (resp.ok) {
    const json = (await resp.json()) as ApiResponse<SignUpResponse>;
    console.log("REGISTERED: ", json);
    saveCredsInLocalStorage(json.data.access_token, json.data.refresh_token);
    await claimPendingAttribution(false, 0, true);
    clearAcquisitionDealAfterSignup();
    return true;
  }
  const text = await resp.text();
  throw new Error(text);
}

// export async function verifyUser(verificationCode: string) {
//   const response = await FetchClient.post("/verify", {
//     verificationCode,
//   });

//   return response as User;
// }

export async function resendEmail() {
  return FetchClient.get("/resend-email");
}

export async function requestChangePassword(email: string) {
  return FetchClient.post("/auth/password-request", { email }, {}, true);
}
export async function requestChangeConfirm(
  email: string,
  code: string,
  newPassword: string
) {
  return FetchClient.post(
    "/auth/password-confirm",
    { email, confirmationCode: code, newPassword },
    {},
    true
  );
}

export async function claimPendingAttribution(
  notifySignup = false,
  waitMs = 0,
  includeSignupReferrer = false,
) {
  if (waitMs > 0) await waitForReferral(waitMs);
  const referralId = getReferralForSignup();
  const acquisitionDealId = getAcquisitionDealForSignup();
  const signupReferrerDomain = includeSignupReferrer
    ? getSignupReferrerDomain()
    : undefined;
  if (!referralId && !acquisitionDealId && !signupReferrerDomain && !notifySignup) return;

  const tokens = getTokensFromStorage();
  if (!tokens?.accessToken) return;

  const body: {
    referralId?: string;
    acquisitionDealId?: string;
    signupReferrerDomain?: string;
    notifySignup?: boolean;
    signupSource?: "Web";
  } = {};
  if (referralId) body.referralId = referralId;
  if (acquisitionDealId) body.acquisitionDealId = acquisitionDealId;
  if (signupReferrerDomain) body.signupReferrerDomain = signupReferrerDomain;
  if (notifySignup) body.notifySignup = true;
  if (notifySignup) body.signupSource = "Web";

  const resp = await fetch(import.meta.env.VITE_API_URL + "/v1/attribution/claim", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: tokens.accessToken,
    },
    body: JSON.stringify(body),
  });

  if (resp.ok) {
    clearReferralAfterClaim();
    clearAcquisitionDealAfterSignup();
    if (includeSignupReferrer) clearSignupReferrer();
  }
}
